import api from '@/services/axiosApi'

export interface ConfigVersionEntry {
  id: number
  version_number: number
  source: 'user' | 'mcp' | 'api' | 'righty'
  source_detail: string | null
  description: string | null
  ai_description: string | null
  checksum: string
  created_by: { name: string; email: string } | null
  created_at: string
}

export interface ConfigPublishEntry {
  id: number
  publish_message: string | null
  published_at: string
  is_live: boolean
}

export interface VersionListResponse {
  versions: ConfigVersionEntry[]
  total: number
  limit: number
  offset: number
  retention: { keep_count: number; daily_days: number }
  /** Only present on the first page (offset 0) */
  publishes?: ConfigPublishEntry[]
}

export interface CreateVersionResponse {
  created: boolean
  version: Pick<
    ConfigVersionEntry,
    'id' | 'version_number' | 'source' | 'source_detail' | 'description' | 'checksum' | 'created_at'
  > | null
}

export const configVersionService = {
  listVersions(teamPid: string | number, offset = 0, limit = 20): Promise<VersionListResponse> {
    return api
      .get(`/api/${teamPid}/configs/versions`, { params: { offset, limit } })
      .then((res) => res.data)
  },
  createVersion(
    teamPid: string | number,
    payload: { sourceDetail?: string | null; description?: string | null }
  ): Promise<CreateVersionResponse> {
    return api
      .post(`/api/${teamPid}/configs/versions`, {
        source_detail: payload.sourceDetail,
        description: payload.description
      })
      .then((res) => res.data)
  },

  getVersionData(teamPid: string | number, versionId: number): Promise<{ data: any }> {
    return api.get(`/api/${teamPid}/configs/versions/${versionId}/data`).then((res) => res.data)
  },

  /** Generate (or fetch the stored) AI description of what changed in a version */
  describeVersion(
    teamPid: string | number,
    versionId: number
  ): Promise<{ description: string | null; stored: boolean }> {
    return api.post(`/api/${teamPid}/configs/versions/${versionId}/describe`).then((res) => res.data)
  },

  restoreVersion(teamPid: string | number, versionId: number): Promise<{ success: boolean; message: string }> {
    return api.post(`/api/${teamPid}/configs/versions/${versionId}/restore`).then((res) => res.data)
  },

  /** Restore the draft to a previously published config */
  restorePublish(teamPid: string | number, configId: number): Promise<{ success: boolean; message: string }> {
    return api.post(`/api/${teamPid}/configs/publishes/${configId}/restore`).then((res) => res.data)
  }
}
